import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { FpShell } from "@/pages/panchatanthra/fp/FpShell";
import { EmptyState } from "@/components/shared";
import { fpBase, inr, num2, useFpContext } from "@/lib/fp";
import { toast } from "sonner";

const STATUS_TONE = {
  Ahead: "bg-success/18 text-success",
  Behind: "bg-destructive/15 text-destructive",
  "Not scored": "bg-muted text-muted-foreground",
};

const show = (value, unit) => {
  if (value === null || value === undefined) return "—";
  if (unit === "₹") return inr(value);
  if (unit === "%") return `${num2(value)}%`;
  if (unit === "x") return `${num2(value)}x`;
  return num2(value);
};

export default function FpParameters() {
  const { tenantId, period, basis } = useFpContext();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});

  const load = useCallback(async () => {
    if (!tenantId || !period) return;
    try {
      const { data: out } = await api.get(`${fpBase(tenantId)}/scorecard`,
                                          { params: { period, basis } });
      setData(out);
      setDraft(Object.fromEntries(out.rows.map((r) =>
        [r.parameter_id, { selected: r.selected, goal: r.goal ?? "" }])));
    } catch (e) { setData({ error: errMsg(e) }); }
  }, [tenantId, period, basis]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.put(`${fpBase(tenantId)}/scorecard/parameters`, {
        items: Object.entries(draft).map(([parameter_id, v]) => ({
          parameter_id, selected: !!v.selected,
          goal: v.goal === "" ? null : Number(v.goal),
        })),
      });
      toast.success("Parameters and goals saved");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const set = (id, patch) => setDraft({ ...draft, [id]: { ...draft[id], ...patch } });
  const selected = (data?.rows || []).filter((r) => draft[r.parameter_id]?.selected);

  return (
    <FpShell
      title="Parameters, goals & actuals"
      description="The Aikyam parameter library is a catalogue, never a client scorecard. Select only the parameters this engagement runs on, set the goal, and the engine reads the actual straight from the approved output pack."
      actions={data?.can_configure && (
        <button onClick={save} data-testid="fp-parameters-save"
                className="rounded-full bg-terracotta px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-white hover:bg-terracotta/90">
          Save selection & goals
        </button>
      )}
    >
      {!period ? (
        <EmptyState testid="fp-parameters-empty" title="Nothing uploaded yet"
                    message="Upload a Profit & Loss or Balance Sheet under Statements first." />
      ) : data?.error ? (
        <EmptyState testid="fp-parameters-error" title="Unavailable" message={data.error} />
      ) : !data ? (
        <p className="label-caps animate-pulse">Reading the parameter library</p>
      ) : (
        <div className="space-y-6" data-testid="fp-parameters">
          <div className="panel overflow-hidden" data-testid="fp-goal-vs-actual">
            <div className="border-b border-border px-6 py-4">
              <p className="label-caps">Goal vs actual · {data.fiscal_year}</p>
              <h3 className="font-display text-lg text-navy">
                {selected.length} parameter{selected.length === 1 ? "" : "s"} active on this engagement
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead className="bg-muted/40 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                  <tr>
                    <th className="px-5 py-2.5 text-left">On</th>
                    <th className="px-5 py-2.5 text-left">Parameter</th>
                    <th className="px-5 py-2.5 text-right">Actual</th>
                    <th className="px-5 py-2.5 text-right">Goal</th>
                    <th className="px-5 py-2.5 text-right">Gap</th>
                    <th className="px-5 py-2.5 text-left">Direction</th>
                    <th className="px-5 py-2.5 text-right">Achieved</th>
                    <th className="px-5 py-2.5 text-right">Evidence score</th>
                    <th className="px-5 py-2.5 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.rows.map((r) => {
                    const d = draft[r.parameter_id] || {};
                    return (
                      <tr key={r.parameter_id} data-testid={`fp-param-${r.parameter_id}`}
                          className={d.selected ? "" : "opacity-60"}>
                        <td className="px-5 py-2.5">
                          <input type="checkbox" checked={!!d.selected}
                                 disabled={!data.can_configure}
                                 data-testid={`fp-param-select-${r.parameter_id}`}
                                 onChange={(e) => set(r.parameter_id, { selected: e.target.checked })}
                                 className="h-4 w-4 accent-[hsl(12_62%_47%)]" />
                        </td>
                        <td className="px-5 py-2.5">
                          <p className="font-medium text-navy">{r.name}</p>
                          <p className="text-[10px] text-muted-foreground">{r.methodology}</p>
                        </td>
                        <td className="px-5 py-2.5 text-right font-mono tabular-nums text-navy"
                            data-testid={`fp-param-actual-${r.parameter_id}`}>
                          {r.available ? show(r.actual, r.unit) : "Not available"}
                        </td>
                        <td className="px-5 py-2.5 text-right">
                          <input type="number" step="any" value={d.goal ?? ""}
                                 disabled={!data.can_configure}
                                 data-testid={`fp-param-goal-${r.parameter_id}`}
                                 onChange={(e) => set(r.parameter_id, { goal: e.target.value })}
                                 className="w-28 rounded-lg border border-border bg-card px-2.5 py-1.5 text-right font-mono text-xs text-navy outline-none focus:border-terracotta disabled:opacity-60" />
                        </td>
                        <td className="px-5 py-2.5 text-right font-mono tabular-nums text-muted-foreground">
                          {show(r.gap, r.unit)}
                        </td>
                        <td className="px-5 py-2.5 text-[11px] text-muted-foreground">{r.direction}</td>
                        <td className="px-5 py-2.5 text-right font-mono tabular-nums">
                          {r.achievement_pct === null ? "—" : `${r.achievement_pct}%`}
                        </td>
                        <td className="px-5 py-2.5 text-right font-mono tabular-nums text-navy"
                            data-testid={`fp-param-evidence-${r.parameter_id}`}>
                          {r.evidence_score10 === null ? "—" : `${r.evidence_score10}/10`}
                        </td>
                        <td className="px-5 py-2.5">
                          <span className={`rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.1em] ${
                            STATUS_TONE[r.status] || "bg-muted"}`}>
                            {r.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="panel p-6" data-testid="fp-evidence-bands">
            <p className="label-caps">Evidence scoring bands in force</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {(data.evidence_bands || []).map((b, i) => (
                <span key={i} className="rounded-full border border-border px-3 py-1.5 text-[11px] text-navy">
                  {b.achievement_from}–{b.achievement_to}% of goal → {b.score}/10
                </span>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">
              Bands are Aikyam configuration, not code. Change them under Admin → Financial
              Performance configuration and every engagement follows.
            </p>
          </div>
        </div>
      )}
    </FpShell>
  );
}
